import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare } from '@medplum/fhirtypes';
import {
  findConflictingOpenEpisode,
  getServiceTypeCode,
  isOpenEpisodeOfCare,
  OPEN_EPISODE_OF_CARE_STATUSES,
} from '@unify/fhir-model/resources/episode-of-care';

/** All open (non-terminal) episodes for a patient. */
export async function fetchOpenEpisodesOfCare(
  medplum: MedplumClient,
  patientId: string,
): Promise<EpisodeOfCare[]> {
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: OPEN_EPISODE_OF_CARE_STATUSES.join(','),
    _count: '100',
  });
  return episodes.filter(isOpenEpisodeOfCare);
}

/** Therapy type codes the patient already has an open episode for (used to disable options). */
export function openTherapyTypeCodes(episodes: EpisodeOfCare[]): Set<string> {
  const codes = new Set<string>();
  for (const e of episodes) {
    const code = getServiceTypeCode(e);
    if (code) codes.add(code);
  }
  return codes;
}

export class DuplicateOpenEpisodeError extends Error {
  constructor(
    readonly existingEpisode: EpisodeOfCare,
    therapyTypeLabel: string,
  ) {
    super(`This client already has an open ${therapyTypeLabel} episode of care.`);
    this.name = 'DuplicateOpenEpisodeError';
  }
}

/**
 * Throws {@link DuplicateOpenEpisodeError} when the patient already has an open episode of
 * this therapy type. Group therapy is exempt (one episode per cohort).
 */
export async function assertNoOpenEpisodeOfType(
  medplum: MedplumClient,
  patientId: string,
  therapyTypeCode: string,
  therapyTypeLabel: string = therapyTypeCode,
): Promise<void> {
  const open = await fetchOpenEpisodesOfCare(medplum, patientId);
  const conflict = findConflictingOpenEpisode(open, therapyTypeCode);
  if (conflict) throw new DuplicateOpenEpisodeError(conflict, therapyTypeLabel);
}
